// Countdown share card — DOM preview + canvas PNG download. No auto-posting.
import { useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getNow, windowState } from "@/lib/windows";

const CROWN_PATH = "M8 68 L16 24 L34 46 L50 14 L66 46 L84 24 L92 68 Z";

const COPY = {
  student: { opens: "RECRUITMENT OPENS", date: "SEPTEMBER 5", dateSm: "September 5, 2026", share: "The recruitment window opens September 5. Big Little Village — Founding Cohort 2027. ✦" },
  talent: null,
  brand: { opens: "NETWORK OPENS", date: "NOVEMBER 1", dateSm: "November 1, 2026", share: "The network window opens November 1. Big Little Village — Founding Cohort 2027. ✦" },
  mentor: null,
};
const copyFor = (kind) => COPY[kind === "talent" ? "student" : kind === "mentor" ? "brand" : kind] || COPY.student;

const daysLeft = (kind) => {
  const w = windowState(kind);
  if (w.status !== "sealed" || !w.open) return null;
  return Math.max(1, Math.ceil((new Date(`${w.open}T00:00:00`) - getNow()) / 86400000));
};

const drawCard = async (kind) => {
  await document.fonts.ready;
  const c = copyFor(kind);
  const days = daysLeft(kind);
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0A0A0B";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.45, 50, W / 2, H * 0.45, 700);
  glow.addColorStop(0, "rgba(201,162,39,0.12)");
  glow.addColorStop(1, "rgba(201,162,39,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(201,162,39,0.6)";
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = "rgba(201,162,39,0.25)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(58, 58, W - 116, H - 116);

  ctx.save();
  ctx.translate(W / 2 - 55, 120);
  ctx.scale(1.1, 1.1);
  ctx.strokeStyle = "#E7C873";
  ctx.lineWidth = 4;
  ctx.lineJoin = "round";
  ctx.stroke(new Path2D(CROWN_PATH));
  ctx.fillStyle = "#C9A227";
  ctx.fillRect(8, 74, 84, 4);
  ctx.restore();

  ctx.textAlign = "center";
  ctx.fillStyle = "#C9A227";
  ctx.font = "600 30px Cinzel, serif";
  ctx.fillText("C L A S S I F I E D   T R A N S M I S S I O N", W / 2, 300);

  const metal = ctx.createLinearGradient(0, 360, 0, 600);
  metal.addColorStop(0, "#F7F8FA");
  metal.addColorStop(0.45, "#D4D7DD");
  metal.addColorStop(0.55, "#8E939C");
  metal.addColorStop(1, "#EDEFF3");
  ctx.fillStyle = metal;
  ctx.font = "116px Anton, Impact, sans-serif";
  ctx.fillText("BIG LITTLE", W / 2, 460);
  ctx.fillText("VILLAGE", W / 2, 586);

  ctx.fillStyle = "#E7C873";
  ctx.font = "600 36px Cinzel, serif";
  ctx.fillText("F O U N D I N G   C O H O R T   2 0 2 7", W / 2, 668);

  const rule = ctx.createLinearGradient(W / 2 - 220, 0, W / 2 + 220, 0);
  rule.addColorStop(0, "rgba(201,162,39,0)");
  rule.addColorStop(0.5, "rgba(201,162,39,0.9)");
  rule.addColorStop(1, "rgba(201,162,39,0)");
  ctx.fillStyle = rule;
  ctx.fillRect(W / 2 - 220, 712, 440, 2);

  ctx.fillStyle = "#F4EFE6";
  ctx.font = "60px Anton, Impact, sans-serif";
  ctx.fillText(c.opens, W / 2, 812);

  const goldGrad = ctx.createLinearGradient(0, 850, 0, 990);
  goldGrad.addColorStop(0, "#E7C873");
  goldGrad.addColorStop(0.5, "#C9A227");
  goldGrad.addColorStop(1, "#E7C873");
  ctx.fillStyle = goldGrad;
  ctx.font = "148px Anton, Impact, sans-serif";
  ctx.fillText(c.date, W / 2, 966);

  if (days) {
    const chip = `T-MINUS ${days} DAY${days === 1 ? "" : "S"}`;
    ctx.font = "700 34px 'JetBrains Mono', monospace";
    const tw = ctx.measureText(chip).width;
    ctx.strokeStyle = "rgba(201,162,39,0.5)";
    ctx.lineWidth = 2;
    ctx.strokeRect(W / 2 - tw / 2 - 28, 1024, tw + 56, 64);
    ctx.fillStyle = "#C9A227";
    ctx.fillText(chip, W / 2, 1068);
  }

  ctx.fillStyle = "rgba(201,162,39,0.7)";
  ctx.font = "600 26px Cinzel, serif";
  ctx.fillText("A   C R E A T I V E   E C O S Y S T E M", W / 2, 1210);

  return canvas;
};

export const CountdownShareCard = ({ kind = "student", preview = true }) => {
  const [busy, setBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const c = copyFor(kind);
  const days = daysLeft(kind);

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await drawCard(kind);
      const a = document.createElement("a");
      a.download = "blv-countdown-card.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success("Your countdown card is downloading ✦");
    } catch {
      toast.error("Couldn't build the card. Try a screenshot instead.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    try {
      await navigator.share({
        title: "Big Little Village — Founding Cohort 2027",
        text: c.share,
        url: window.location.origin,
      });
    } catch {}
  };

  return (
    <div data-testid={`countdown-share-card-${kind}`}>
      {preview && (
        <div className="gold-frame grain-dark relative mx-auto aspect-[4/5] w-full max-w-[260px] bg-onyx p-5 text-center">
          <div className="pointer-events-none absolute inset-2 border border-gold/25" />
          <svg width="36" height="27" viewBox="0 0 100 80" fill="none" className="mx-auto mt-3" aria-hidden="true">
            <path d={CROWN_PATH} stroke="#E7C873" strokeWidth="5" strokeLinejoin="round" />
            <rect x="8" y="72" width="84" height="4" fill="#C9A227" />
          </svg>
          <p className="mt-3 font-cinzel text-[7px] uppercase tracking-[0.4em] text-gold">Classified transmission</p>
          <p className="mt-2 font-display text-2xl uppercase leading-[0.9] tracking-tight text-metallic">Big Little<br />Village</p>
          <p className="mt-2 font-cinzel text-[8px] uppercase tracking-[0.35em] text-goldlight">Founding Cohort 2027</p>
          <div className="gold-rule mx-auto mt-2.5 w-20" />
          <p className="mt-3 font-display text-sm uppercase tracking-tight text-cream">{c.opens}</p>
          <p className="font-display text-2xl uppercase tracking-tight text-gold-metallic" data-testid="countdown-card-date">{c.date}</p>
          {days && (
            <p className="mt-2 inline-block border border-gold/40 px-2.5 py-1 font-mono text-[8px] font-bold uppercase tracking-[0.25em] text-gold" data-testid="countdown-card-tminus">
              T-minus {days} day{days === 1 ? "" : "s"}
            </p>
          )}
        </div>
      )}
      <div className={`${preview ? "mt-4" : ""} flex flex-col items-center justify-center gap-3 sm:flex-row`}>
        <button
          onClick={download}
          disabled={busy}
          data-testid="download-countdown-card-btn"
          className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-6 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
        >
          <Download size={14} /> {busy ? "Building..." : "Get the Countdown Card"}
        </button>
        {canShare && (
          <button
            onClick={share}
            data-testid="share-countdown-btn"
            className="inline-flex items-center gap-2 border border-silver/40 px-6 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight"
          >
            <Share2 size={14} /> Spread the Word
          </button>
        )}
      </div>
    </div>
  );
};
